import React from "react";
import Highcharts, { dateFormat } from "highcharts";

import { Box } from "@material-ui/core";

import StockLine from "components/visuals/StockLine";
import useColourPalette from "components/visuals/useColourPalette";

import { useAppDispatch, useAppSelector } from "store";
import { selectSelectedStoreByCostType, selectUseCustomCalendar } from "modules/customer/insights/cost/costSlice";
import {
    selectCostMeasure,
    selectCostsOverTime,
    setCostsOverTimeLineChartGranularity,
    selectCostsOverTimeLineChartGranularity
} from "modules/customer/insights/cost/storeCosts/storeCostsSlice";
import { CostMeasureType } from "modules/customer/insights/cost/costMeasureType";
import { CostGranularity } from "modules/customer/insights/cost/costGranularity";

import { DateTime } from "luxon";
import numberFormatter from "utils/numberFormatter";
import stringUtils from "utils/stringUtils";
import { abs, isString } from "mathjs";

const CostsOverTimeLineChart: React.FC = () => {
    const dispatch = useAppDispatch();
    const colourPalette = useColourPalette();
    const selectedStore = useAppSelector(selectSelectedStoreByCostType);
    const costMeasure = useAppSelector(selectCostMeasure);
    const costsOverTime = useAppSelector(selectCostsOverTime);
    const chartGranularity = useAppSelector(selectCostsOverTimeLineChartGranularity);
    const useCustomCalendar = useAppSelector(selectUseCustomCalendar);

    const series: Highcharts.SeriesOptionsType[] = [];

    const selectedButton = chartGranularity === CostGranularity.Month
        ? 0
        : chartGranularity === CostGranularity.Quarter ? 1 : 2;

    const seriesData = costsOverTime.data.store;

    const getCustomLabel = (timestamp: string | number, granularity: CostGranularity) => {
        if (isString(timestamp) || seriesData.length === 0) {
            return String(timestamp);
        }
        // Find closest date in dates array
        let difference = Number.MAX_VALUE;
        let dateIndex = 0;
        seriesData.forEach((item, index) => {
            const newDifference = abs(timestamp - item.date.toMillis());
            if (newDifference < difference) {
                difference = newDifference;
                dateIndex = index;
            }
        });
        if (granularity === CostGranularity.Month) {
            return seriesData[dateIndex].customDate.dateLabel;
        }
        if (granularity === CostGranularity.Quarter) {
            return seriesData[dateIndex].customDate.quarterLabel;
        }
        if (granularity === CostGranularity.Year) {
            return String(seriesData[dateIndex].customDate.financialYearLabel);
        }
        return seriesData[dateIndex].customDate.dateLabel;
    };

    //ToDo: Use separate date array containing all period dates
    const tickPositions = seriesData.map(item => item.customDate.date.toMillis());

    const navigatorTickPositions = chartGranularity === CostGranularity.Month
        ? [...seriesData.filter(item => item.customDate.periodNumber === 7).map(item => item.customDate.date.toMillis())]
        : chartGranularity === CostGranularity.Quarter
            ? [...seriesData.filter(item => item.customDate.quarterNumber === 3).map(item => item.customDate.date.toMillis())]
            : undefined;

    series.push({
        type: "spline",
        showInNavigator: true,
        name: `${selectedStore?.storeName}`,
        data: costsOverTime.data.store.map(store =>
            [DateTime.fromISO(store.date.toISODate(), { zone: "utc" }).toMillis(), (costMeasure === CostMeasureType.CostValue ? store.costValue : store.costAsPercentageOfRevenue)]
        ),
        color: colourPalette.comparators[0]
    });

    series.push({
        type: "spline",
        showInNavigator: true,
        name: `Cluster average`,
        data: costsOverTime.data.clusterAverage.map(store =>
            [DateTime.fromISO(store.date.toISODate(), { zone: "utc" }).toMillis(), (costMeasure === CostMeasureType.CostValue ? store.costValue : store.costAsPercentageOfRevenue)]
        ),
        color: colourPalette.comparators[1]
    });

    const options: Highcharts.Options = {
        xAxis: {
            type: 'datetime',
            ...(useCustomCalendar && {
                labels: {
                    formatter: function () {
                        return getCustomLabel(this.value, chartGranularity);
                    }
                },
                tickPositions
            })
        },
        yAxis: {
            title: {
                text: (costMeasure === CostMeasureType.CostValue ? "Costs" : "Cost as a % of revenue")
            },
            labels: {
                formatter: function () {
                    return (
                        costMeasure === CostMeasureType.CostValue ?
                            numberFormatter.toGBP(this.value, 0) :
                            numberFormatter.toPercentage(this.value, true, 0)
                    );
                }
            }
        },
        legend: {
            enabled: true
        },
        series,
        navigator: {
            series: {
                fillColor: 'none',
                fillOpacity: 0
            },
            ...(useCustomCalendar && {
                xAxis: {
                    showFirstLabel: true,
                    labels: {
                        formatter: function () {
                            return getCustomLabel(this.value, CostGranularity.Year);
                        }
                    },
                    tickPositions: navigatorTickPositions
                }
            })
        },
        rangeSelector: {
            allButtonsEnabled: true,
            selected: selectedButton,
            buttons: [{
                type: "month",
                count: 12,
                text: useCustomCalendar ? "Period" : "Month",
                dataGrouping: {
                    enabled: false
                },
                events: {
                    click: function () {
                        dispatch(setCostsOverTimeLineChartGranularity(CostGranularity.Month));
                    }
                },
                preserveDataGrouping: true
            }, {
                type: "month",
                count: 24,
                text: "Quarter",
                dataGrouping: {
                    enabled: false
                },
                events: {
                    click: function () {
                        dispatch(setCostsOverTimeLineChartGranularity(CostGranularity.Quarter));
                    }
                },
                preserveDataGrouping: true
            }, {
                type: "year",
                count: 3,
                text: useCustomCalendar ? "FY" : "Year",
                dataGrouping: {
                    enabled: false
                },
                events: {
                    click: function () {
                        dispatch(setCostsOverTimeLineChartGranularity(CostGranularity.Year));
                    }
                },
                preserveDataGrouping: true
            }]
        },
        tooltip: {
            enabled: true,
            shared: true,
            headerFormat: ``,
            footerFormat: ``,
            formatter: function () {
                if (!this.points) {
                    return "";
                }
                let header = "";
                const timestamp = this.points[0].x as number;
                if (useCustomCalendar) {
                    header = getCustomLabel(timestamp, chartGranularity);
                }
                else if (chartGranularity === CostGranularity.Year) {
                    header = dateFormat("%Y", timestamp);
                }
                else if (chartGranularity === CostGranularity.Month) {
                    header = dateFormat("%B %Y", timestamp);
                }
                else {
                    const month = DateTime.fromMillis(timestamp).month;
                    const quarter = Math.ceil(month / 3);
                    header = `Q${quarter} ` + dateFormat("%Y", timestamp);
                }

                const categoriesArr: string[] = [];
                const categoriesFormatArr: string[] = [];
                const valuesArr: string[] = [];

                this.points.forEach((point, index) => {
                    categoriesArr.push(point.series.name, (costMeasure === CostMeasureType.CostValue ? "Cost value" : "Cost as % revenue"));
                    categoriesFormatArr.push(`color:${point.series.color};font-weight:bold`, "");

                    const value = costMeasure === CostMeasureType.CostValue ? numberFormatter.toGBP(point.y) : numberFormatter.toPercentage(point.y, true, 1);

                    valuesArr.push(
                        "",
                        value
                    );
                });

                return `<table>${stringUtils.tooltipHTML(
                    categoriesArr,
                    {
                        header: header,
                        values: valuesArr,
                        categoryFormattingArr: categoriesFormatArr
                    }
                )}</table>`;
            },
            useHTML: true
        }
    };

    return (
        <Box data-cy="store-costs-over-time">
            <StockLine loading={costsOverTime.isLoading} error={costsOverTime.hasErrors} options={options} globalOptions={{}} />
        </Box>
    );
};

export default CostsOverTimeLineChart;
